import {
  whatsAppApi,
  type WhatsAppTemplateEntity,
  type WhatsAppTemplateGroup,
} from '../../api/WhatsAppApi';

export type WhatsAppTemplateRecord = WhatsAppTemplateEntity;

export type WhatsAppGroupRecord = {
  group: WhatsAppTemplateGroup;
  templates: WhatsAppTemplateRecord[];
};

const GROUP_ORDER: WhatsAppTemplateGroup[] = ['booking', 'rental', 'customer'];

/**
 * Sample values for the editor's live preview.
 *
 * Deliberately concrete rather than `{{placeholder}}` echoes — a merchant needs to
 * see the shape of the finished message, including how the conditional blocks fall.
 */
export const PREVIEW_SAMPLE: Record<string, string> = {
  customerName: 'Budi Santoso',
  tenantName: 'Budi Santoso',
  staffName: 'Sari',
  projectName: 'BatamX',
  serviceName: 'Cuci Mobil Premium',
  assetName: 'Toyota Avanza — BP 1234 XY',
  bookingDate: '25 Agustus 2026',
  bookingTime: '14:00',
  startDate: '25 Agustus 2026',
  endDate: '25 September 2026',
  dueDate: '1 September 2026',
  paidDate: '1 September 2026',
  returnDate: '20 September 2026',
  daysOverdue: '3',
  totalPrice: 'Rp 150.000',
  amount: 'Rp 1.500.000',
  rate: 'Rp 1.500.000 / bulan',
  depositAmount: 'Rp 500.000',
  refundDue: 'Rp 250.000',
  finalTotal: 'Rp 1.250.000',
  address: 'Jl. Engku Putri No. 1, Batam',
  invoiceUrl: 'https://batamx.co/invoice/a1b2c3',
  bookingUrl: 'https://batamx.co/book/a1b2c3',
  invoiceNotes: 'Pembayaran dapat dilakukan melalui transfer bank.',
};

/**
 * Fills a template the way the app will at send time.
 *
 * Mirrors `whatsappTemplate.ts` in the booking app: conditionals first, so a block
 * whose variable is empty disappears before substitution runs over what is left.
 */
export function renderPreview(body: string, values: Record<string, string>): string {
  const isTruthy = (value: string | undefined) => {
    const raw = (value ?? '').trim();
    if (!raw) return false;
    return !/^0+(\.0+)?$/.test(raw);
  };

  const splitElse = (segment: string): [string, string] => {
    const index = segment.search(/\{\{\s*else\s*\}\}/);
    if (index === -1) return [segment, ''];
    return [
      segment.slice(0, index),
      segment.slice(index).replace(/\{\{\s*else\s*\}\}/, ''),
    ];
  };

  const trimOneNewline = (text: string) => {
    let out = text;
    if (out.startsWith('\n')) out = out.slice(1);
    if (out.endsWith('\n')) out = out.slice(0, -1);
    return out;
  };

  let out = body || '';

  const standalone = /^[ \t]*\{\{#if\s+([a-zA-Z0-9_]+)\s*\}\}[ \t]*\n([\s\S]*?)^[ \t]*\{\{\s*\/if\s*\}\}[ \t]*(\n|$)/gm;
  out = out.replace(standalone, (_m, key: string, segment: string, suffix: string) => {
    const [truthy, falsy] = splitElse(segment);
    const chosen = trimOneNewline(isTruthy(values[key]) ? truthy : falsy);
    if (!chosen.trim()) return '';
    return suffix === '\n' && !chosen.endsWith('\n') ? `${chosen}\n` : chosen;
  });

  const inline = /\{\{#if\s+([a-zA-Z0-9_]+)\s*\}\}([\s\S]*?)\{\{\s*\/if\s*\}\}/g;
  let previous: string | undefined;
  while (previous !== out) {
    previous = out;
    // Not trimmed, unlike the standalone case: the newline before an inline `{{/if}}` is
    // the separator the author typed, and swallowing it glued the invoice notes to the
    // sign-off in the message customers received.
    out = out.replace(inline, (_m, key: string, segment: string) => {
      const [truthy, falsy] = splitElse(segment);
      return isTruthy(values[key]) ? truthy : falsy;
    });
  }

  Object.entries(values).forEach(([key, value]) => {
    const safe = value ?? '';
    out = out
      .replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), safe)
      .replace(new RegExp(`\\$\\{\\s*${key}\\s*\\}`, 'g'), safe)
      .replace(new RegExp(`\\[\\[\\s*${key}\\s*\\]\\]`, 'g'), safe)
      .replace(new RegExp(`(?<![{$[])\\{\\s*${key}\\s*\\}`, 'g'), safe);
  });

  // Anything still unresolved would ship literal braces to a customer.
  return out.replace(/\{\{\s*[a-zA-Z0-9_]+\s*\}\}/g, '');
}

class WhatsAppStore {
  /** Grouped for the accordion, in a fixed order rather than whatever the API returned. */
  private toGroups(templates: WhatsAppTemplateRecord[]): WhatsAppGroupRecord[] {
    return GROUP_ORDER
      .map((group) => ({
        group,
        templates: templates.filter((template) => template.group === group),
      }))
      .filter((entry) => entry.templates.length > 0);
  }

  async fetch(): Promise<WhatsAppGroupRecord[]> {
    const { data } = await whatsAppApi.catalogue();
    return this.toGroups(data.data ?? []);
  }

  async save(code: string, body: string, enabled: boolean): Promise<WhatsAppTemplateRecord> {
    const { data } = await whatsAppApi.upsert(code, { body, enabled });
    return data;
  }

  async reset(code: string): Promise<WhatsAppTemplateRecord> {
    const { data } = await whatsAppApi.reset(code);
    return data;
  }
}

export const whatsAppStore = new WhatsAppStore();
